/**
 * Dynamic business settings. README rule 7: every business parameter is a
 * setting read from the database. Nothing in this file is a live value — it
 * declares the *key*, its *type*, and a *seed* used only to populate a fresh
 * database. All runtime reads go through the Setting service, and every change
 * is audit-logged.
 *
 * Seed values are owner decisions (`docs/DECISIONS.md` D18, D25). They remain
 * overridable by an ADMIN and are additionally enforced by database CHECKs.
 *
 * Deliberately absent: `grace_period_hours` and `school_year_seats` (D16, D25).
 * There is no fixed school year — one seat is one subject for one month — and
 * plans are an admin-managed package catalog, not a setting (D17).
 */
import { z } from 'zod';

export const SETTING_DEFS = {
  // --- Money ---
  /** Cost of one seat: one subject for one month (12 live classes). */
  seat_price_cents: z.coerce.number().int().positive(),
  min_donation_cents: z.coerce.number().int().positive(),
  /** Upper bound for card donations ONLY. Bank transfers are not bounded by it (D26). */
  max_card_donation_cents: z.coerce.number().int().positive(),

  // --- Gateway fees (README rule 15, owner decision D18) ---
  /** Basis points of the payer amount taken as fee. 300 = 3%. */
  fee_percent_bps: z.coerce.number().int().nonnegative(),
  /** Flat component of the fee, in cents. */
  fee_fixed_cents: z.coerce.number().int().nonnegative(),
  /** Whether the cover-fees checkbox starts checked. */
  cover_fees_default: z.coerce.boolean(),

  // --- Code lifecycle (durations in days; timestamps are stored per row) ---
  code_lifetime_days: z.coerce.number().int().positive(),
  notice_period_days: z.coerce.number().int().nonnegative(),

  // --- Privacy (rule 11) ---
  /** Aggregates for a group smaller than this are suppressed entirely. */
  privacy_min_group_size: z.coerce.number().int().positive(),

  // --- Identity / currency ---
  base_currency: z.string().length(3),

  // --- Organizations (03_growth_layer.md §B) ---
  invite_expiry_days: z.coerce.number().int().positive(),

  // --- Feature flags ---
  recurring_enabled: z.coerce.boolean(),

  // --- Founding donors (03_growth_layer.md §D.8) ---
  founding_window_start: z.coerce.date().nullable(),
  founding_window_end: z.coerce.date().nullable(),

  // --- Bank-transfer instructions (admin-managed, D28 / Phase 3) ---
  bank_account_holder: z.string(),
  bank_name: z.string(),
  bank_iban: z.string(),
  bank_currency: z.literal('USD'),
  bank_instructions_ar: z.string(),
  /**
   * The same instructions in English.
   *
   * Required and seeded EMPTY, exactly like `bank_instructions_ar`: an invented
   * instruction is worse than a missing one, and the donor site is bilingual
   * with English as the fallback for a browser that is not Arabic. Before this
   * setting exists an English-speaking donor would be shown the Arabic text or
   * nothing at all.
   */
  bank_instructions_en: z.string(),

  /**
   * How long a Decision Manager review may sit before the authorization is
   * released automatically, in hours.
   *
   * The owner's requirement is that nobody has to log into CyberSource or this
   * admin panel every day. An unresolved review is holding a donor's money, and
   * holding it indefinitely is the one part of this flow the system can fix by
   * itself: after this many hours the reconciliation job calls
   * `authReversal`, fails the attempt with `review_expired`, and tells the donor
   * to try again.
   *
   * Default 72. Not a business parameter so much as a safety one: three days is
   * long enough for a person to look at a review queue and short enough that a
   * forgotten case is not a donor's money for a month.
   */
  review_auto_reverse_hours: z.coerce.number().int().positive(),

  /**
   * What happens to an order that Decision Manager sends to review.
   *
   * A **setting and not a code path**, because the owner's instruction is that
   * nobody logs into CyberSource or watches a queue after launch. One policy is
   * chosen at setup and the system applies it on its own.
   *
   * - `reverse_unless_authenticated` — the default, and the one that cannot cost
   *   real money. Capture only when 3-D Secure actually authenticated the
   *   cardholder (a CAVV/UCAF and a documented ECI); otherwise release the
   *   authorization immediately and tell the donor.
   * - `always_reverse` — release every reviewed order. Safest, and it declines
   *   legitimate donors whose cards merely looked unusual.
   * - `always_capture` — take the money on every reviewed order, overriding the
   *   fraud engine entirely. The admin UI says so in those words.
   */
  review_policy: z.enum(['reverse_unless_authenticated', 'always_reverse', 'always_capture']),

  // --- Email (owner-gated) ---
  /**
   * Whether donor receipts may be SENT by email.
   *
   * The template and the transport exist (services/email.ts); nothing calls
   * them while this is false, which is the default. The owner turns it on
   * only after the provider, the sender address and the wording are approved
   * - a receipt is donor-visible correspondence, and correspondence the
   * donor did not ask for is not ours to experiment with.
   */
  receipt_email_enabled: z.coerce.boolean(),

  // --- Guest quick donation (owner-approved, 01_architecture.md §11) ---
  /**
   * Donations one IP may start inside {@link guest_donation_rate_window_seconds}.
   *
   * The unauthenticated surface has to be bounded from the server side: without
   * this, anyone can create unlimited PENDING donations and drive the ledger's
   * open-ended write load for free.
   */
  guest_donation_rate_limit_per_ip: z.coerce.number().int().positive(),
  /** Donations one email address may start in the same window. */
  guest_donation_rate_limit_per_email: z.coerce.number().int().positive(),
  /** The window both limits are counted over, in seconds. */
  guest_donation_rate_window_seconds: z.coerce.number().int().positive(),
  /**
   * How long a PENDING donation may sit before the expiry sweep ends it.
   *
   * A guest who abandons card entry leaves a PENDING row forever otherwise. It
   * is set to EXPIRED — never deleted, because the ledger is append-only and
   * because a deleted row cannot be audited.
   */
  guest_donation_max_pending_age_hours: z.coerce.number().int().positive(),

  // --- Growth ---
  share_card_template: z.string().min(1),

  // --- Fraud controls (owner: build the controls, not just the document) ---
  /**
   * How many payment-surface calls one IP may make inside the window.
   *
   * The guest creation limits above bound how many *donations* one address may
   * start. These bound the rest of the payment surface - `auth-setup`,
   * `auth-enroll` and `pay` - where each call is a gateway round trip and
   * therefore the thing a card tester actually burns.
   */
  payment_rate_limit_per_ip: z.coerce.number().int().positive(),
  /** The same, for one email address, whatever the IP is. */
  payment_rate_limit_per_email: z.coerce.number().int().positive(),
  /** The window both payment-surface limits are counted over, in seconds. */
  payment_rate_limit_window_seconds: z.coerce.number().int().positive(),
  /**
   * How many payment attempts one donation may have.
   *
   * A donor whose card is declined may try again; a script that owns a donation
   * id may try forever. Three is enough for a real donor (a wrong expiry, a
   * wrong CVC, a different card) and not enough for a search.
   */
  max_attempts_per_donation: z.coerce.number().int().positive(),
  /**
   * How long one donation waits between attempts, in seconds.
   *
   * The cooldown is what makes the cap above a budget rather than a cliff: a
   * donor who mistypes their card is not locked out for good, and a script
   * cannot spend the budget faster than the clock allows.
   */
  attempt_cooldown_seconds: z.coerce.number().int().nonnegative(),
  /**
   * How many declined or failed payments inside the window raise the alarm.
   */
  fraud_alarm_decline_threshold: z.coerce.number().int().positive(),
  /** The window the decline count is taken over, in minutes. */
  fraud_alarm_window_minutes: z.coerce.number().int().positive(),
  /**
   * How long after the last decline the bot check stays mandatory, in minutes.
   *
   * The alarm itself is a count inside a window; this is the part that must
   * outlast it, because the attack that trips the alarm does not stop the
   * moment the count falls below the threshold.
   */
  fraud_alarm_bot_check_minutes: z.coerce.number().int().nonnegative(),
} as const;

export type SettingKey = keyof typeof SETTING_DEFS;

export const SETTING_KEYS = Object.keys(SETTING_DEFS) as SettingKey[];

export function isSettingKey(value: string): value is SettingKey {
  return Object.hasOwn(SETTING_DEFS, value);
}

/** Keys the database CHECK constrains to a positive integer. */
export const POSITIVE_INT_SETTING_KEYS = [
  'seat_price_cents',
  'min_donation_cents',
  'max_card_donation_cents',
  'code_lifetime_days',
  'privacy_min_group_size',
  'invite_expiry_days',
  'guest_donation_rate_limit_per_ip',
  'guest_donation_rate_limit_per_email',
  'guest_donation_rate_window_seconds',
  'guest_donation_max_pending_age_hours',
  'payment_rate_limit_per_ip',
  'payment_rate_limit_per_email',
  'payment_rate_limit_window_seconds',
  'max_attempts_per_donation',
  'fraud_alarm_decline_threshold',
  'fraud_alarm_window_minutes',
] as const satisfies readonly SettingKey[];

/** Fresh-database seed values. Never read at runtime. */
export const SETTING_SEEDS = {
  // One seat = one subject for one month (12 live classes).
  seat_price_cents: 500,
  min_donation_cents: 500,
  max_card_donation_cents: 200_000,
  fee_percent_bps: 300,
  fee_fixed_cents: 30,
  cover_fees_default: false,
  code_lifetime_days: 45,
  notice_period_days: 14,
  privacy_min_group_size: 5,
  base_currency: 'USD',
  invite_expiry_days: 7,
  recurring_enabled: false,
  founding_window_start: null,
  founding_window_end: null,
  share_card_template: 'default',
  bank_account_holder: '',
  bank_name: '',
  bank_iban: '',
  bank_currency: 'USD',
  bank_instructions_ar: '',
  bank_instructions_en: '',
  review_auto_reverse_hours: 72,
  review_policy: 'reverse_unless_authenticated',
  guest_donation_rate_limit_per_ip: 10,
  guest_donation_rate_limit_per_email: 3,
  guest_donation_rate_window_seconds: 3_600,
  guest_donation_max_pending_age_hours: 72,
  payment_rate_limit_per_ip: 10,
  payment_rate_limit_per_email: 5,
  payment_rate_limit_window_seconds: 300,
  max_attempts_per_donation: 3,
  attempt_cooldown_seconds: 60,
  fraud_alarm_decline_threshold: 5,
  fraud_alarm_window_minutes: 15,
  fraud_alarm_bot_check_minutes: 60,
  receipt_email_enabled: false,
} as const satisfies Record<SettingKey, unknown>;

/** Parse a stored setting value against its declared type. */
export function parseSetting<K extends SettingKey>(
  key: K,
  value: unknown,
): z.infer<(typeof SETTING_DEFS)[K]> {
  if (!isSettingKey(key)) {
    throw new Error(`unknown setting key: ${String(key)}`);
  }
  return SETTING_DEFS[key].parse(value) as z.infer<(typeof SETTING_DEFS)[K]>;
}
